import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users, Shield, ShieldOff, Key, ArrowLeft, Search,
  CheckCircle, XCircle, RefreshCw, Lock, Unlock
} from "lucide-react";

const API = window.location.origin;

export default function Admin() {
  const navigate = useNavigate();
  const [testers, setTesters] = useState([]);
  const [stats, setStats] = useState({ total: 0, beta_count: 0, has_password_count: 0, locked_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchTesters = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/testers`);
      if (res.ok) {
        const data = await res.json();
        setTesters(data.testers || []);
        setStats({ total: data.total, beta_count: data.beta_count, has_password_count: data.has_password_count, locked_count: data.locked_count });
      } else {
        toast.error("Failed to load testers");
      }
    } catch {
      toast.error("Network error loading testers");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTesters(); }, [fetchTesters]);

  const toggleBeta = async (userId) => {
    setActionLoading(userId + "-beta");
    try {
      const res = await fetch(`${API}/api/admin/testers/${userId}/toggle-beta`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Beta ${data.is_beta_user ? "enabled" : "disabled"}`);
        fetchTesters();
      } else {
        toast.error("Failed to toggle beta");
      }
    } catch {
      toast.error("Network error");
    }
    setActionLoading(null);
  };

  const setPassword = async (userId) => {
    const tempPassword = window.prompt("Enter temporary password for this user:");
    if (!tempPassword) return;
    setActionLoading(userId + "-pw");
    try {
      const res = await fetch(`${API}/api/admin/testers/${userId}/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: tempPassword })
      });
      if (res.ok) {
        toast.success("Temp password set successfully");
        fetchTesters();
      } else {
        toast.error("Failed to set password");
      }
    } catch {
      toast.error("Network error");
    }
    setActionLoading(null);
  };

  const fixAllLocked = async () => {
    setActionLoading("fix-all");
    try {
      const res = await fetch(`${API}/api/admin/testers/fix-all-locked`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Fixed ${data.fixed} locked accounts`);
        fetchTesters();
      } else {
        toast.error("Failed to fix locked accounts");
      }
    } catch {
      toast.error("Network error");
    }
    setActionLoading(null);
  };

  const filtered = testers.filter(t =>
    t.email.toLowerCase().includes(search.toLowerCase()) ||
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-[#555] hover:text-[#111]" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-[#111] font-bold text-lg">Admin Panel</h1>
            <Badge className="bg-amber-500/20 text-[#111] border-amber-500/30 text-xs">Beta</Badge>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-white/20 text-[#111] hover:bg-gray-200"
            onClick={() => { setLoading(true); fetchTesters(); }}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          {[
            { label: "Total Users", value: stats.total, icon: <Users className="w-4 h-4" />, color: "text-blue-400 bg-blue-500/10" },
            { label: "Beta Active", value: stats.beta_count, icon: <Shield className="w-4 h-4" />, color: "text-emerald-700 bg-emerald-500/10" },
            { label: "Has Password", value: stats.has_password_count, icon: <Unlock className="w-4 h-4" />, color: "text-green-400 bg-green-500/10" },
            { label: "Locked Out", value: stats.locked_count, icon: <Lock className="w-4 h-4" />, color: "text-red-400 bg-red-500/10" },
          ].map((s, i) => (
            <Card key={i} className="bg-white border-gray-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}>{s.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-[#111]" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>{s.value}</p>
                  <p className="text-xs text-[#555]">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-gray-100 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
              data-testid="admin-search-input"
            />
          </div>
          {stats.locked_count > 0 && (
            <Button
              onClick={fixAllLocked}
              disabled={actionLoading === "fix-all"}
              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
              data-testid="fix-all-locked-btn"
            >
              <Key className="w-4 h-4 mr-1" />
              {actionLoading === "fix-all" ? "Fixing..." : `Fix ${stats.locked_count} Locked`}
            </Button>
          )}
        </div>

        {/* User list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-user-list">
            {filtered.length === 0 && (
              <p className="text-[#555] text-center py-8" data-testid="no-results">No users found</p>
            )}
            {filtered.map((t) => (
              <Card key={t.user_id} className="bg-white border-gray-200 hover:border-white/20 transition-colors" data-testid={`user-row-${t.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[#111] font-medium text-sm truncate" data-testid={`user-name-${t.user_id}`}>
                          {t.name || "No Name"}
                        </p>
                        {t.is_beta_user && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[10px] px-1.5 py-0">Beta</Badge>}
                        {t.role === "admin" && <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] px-1.5 py-0">Admin</Badge>}
                        {t.role === "coach" && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] px-1.5 py-0">Coach</Badge>}
                      </div>
                      <p className="text-[#555] text-xs truncate" data-testid={`user-email-${t.user_id}`}>{t.email}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-[#555]">
                        <span className="flex items-center gap-1">
                          {t.has_password ? <CheckCircle className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                          {t.has_password ? "Password set" : "No password"}
                        </span>
                        <span>{t.auth_provider}</span>
                        {t.onboarding_complete && <span className="text-emerald-700">Onboarded</span>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`text-xs h-8 ${t.is_beta_user
                          ? "border-red-500/30 text-red-400 hover:bg-red-500/10"
                          : "border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/10"
                        }`}
                        onClick={() => toggleBeta(t.user_id)}
                        disabled={actionLoading === t.user_id + "-beta"}
                        data-testid={`toggle-beta-${t.user_id}`}
                      >
                        {t.is_beta_user ? <ShieldOff className="w-3 h-3 mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
                        {actionLoading === t.user_id + "-beta" ? "..." : t.is_beta_user ? "Revoke" : "Grant"}
                      </Button>
                      {!t.has_password && (
                        <Button
                          size="sm"
                          className="bg-amber-500/20 text-[#111] hover:bg-amber-500/30 border border-amber-500/30 text-xs h-8"
                          onClick={() => setPassword(t.user_id)}
                          disabled={actionLoading === t.user_id + "-pw"}
                          data-testid={`set-password-${t.user_id}`}
                        >
                          <Key className="w-3 h-3 mr-1" />
                          {actionLoading === t.user_id + "-pw" ? "..." : "Set Password"}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
